import { debounce } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import API from 'api/API';
import { HistoricalSwitch } from 'api/models/Deliveries';
import { NamedSelectOption } from 'components/DropDown/ValidatingSelect';

export type OrderNumberCompoundKeyFields = {
  orderNumber?: string;
  supplier?: NamedSelectOption;
};

const useValidateOrderNumber = <T extends OrderNumberCompoundKeyFields>(fields: T) => {
  const [orderNumberValid, setOrderNumberValid] = useState(true);
  const [isValidatingOrderNumber, setIsValidatingOrderNumber] = useState(false);
  const validateOrderNumberRequestId = useRef(0);

  const validateOrderNumber = useCallback(
    async (orderNumber: string) => {
      const trimmedOrderNumber = orderNumber.trim();

      if (!trimmedOrderNumber || !fields.supplier?.id) {
        setOrderNumberValid(true);
        setIsValidatingOrderNumber(false);
        return;
      }

      const requestId = ++validateOrderNumberRequestId.current;
      setIsValidatingOrderNumber(true);

      try {
        const { result } = await API.orders.getOrders({
          offset: '0',
          limit: '1000',
          tenant_id: fields.supplier.id,
          search: trimmedOrderNumber,
          historical: HistoricalSwitch.All,
        });

        if (requestId != validateOrderNumberRequestId.current) return;

        // the search matches partially and across several fields, so the order
        //  number match is narrowed down here
        const isDuplicate = result.some(
          order => order.orderNumber?.trim().toLowerCase() == trimmedOrderNumber.toLowerCase(),
        );

        setOrderNumberValid(!isDuplicate);
        setIsValidatingOrderNumber(false);
      } catch (err) {
        if (requestId == validateOrderNumberRequestId.current) {
          setOrderNumberValid(true);
          setIsValidatingOrderNumber(false);
        }
        API.errorHandler(err);
      }
    },
    [fields.supplier?.id],
  );

  const validateOrderNumberDebounced = useMemo(() => debounce(validateOrderNumber, 400), [validateOrderNumber]);

  const validateOrderNumberDebouncedWithPending = useCallback(
    (orderNumber: string) => {
      const trimmedOrderNumber = orderNumber.trim();
      if (trimmedOrderNumber && fields.supplier?.id) {
        setIsValidatingOrderNumber(true);
      }
      validateOrderNumberDebounced(orderNumber);
    },
    [fields.supplier?.id, validateOrderNumberDebounced],
  );

  return {
    orderNumberValid,
    isValidatingOrderNumber,
    validateOrderNumber,
    validateOrderNumberDebounced: validateOrderNumberDebouncedWithPending,
  };
};

export default useValidateOrderNumber;
